import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import authService from '@/services/authService'
import { teamsAuthService } from '@/services/teamsAuth'
import type { AccountInfo } from '@azure/msal-browser'

export function useAuth() {
  const router = useRouter()
  const user = ref<AccountInfo | null>(null)
  const userProfile = ref<{ displayName: string; mail: string; userPrincipalName: string } | null>(
    null,
  )
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const isInTeams = ref(false)

  const isAuthenticated = computed(() => user.value !== null)

  // Initialize authentication on mount
  onMounted(async () => {
    await initializeAuth()
  })

  const initializeAuth = async () => {
    try {
      isLoading.value = true
      await authService.initialize()

      // Check if running in Teams context
      isInTeams.value = await teamsAuthService.isRunningInTeams()

      if (isInTeams.value) {
        // Initialize Teams auth service
        const teamsConfig = {
          clientId: import.meta.env.VITE_AZURE_CLIENT_ID || '',
          tenantId: import.meta.env.VITE_AZURE_TENANT_ID || 'common',
          redirectUri: import.meta.env.VITE_REDIRECT_URI || window.location.origin,
        }
        await teamsAuthService.initialize(teamsConfig)

        // Check if user is already authenticated (from redirect)
        const account = authService.getCurrentAccount()
        if (account) {
          user.value = account
          await loadUserProfile()
        } else {
          // Check if auto-login is enabled and attempt it
          const autoLoginEnabled = localStorage.getItem('teams_auto_login') !== 'false'
          if (autoLoginEnabled) {
            await attemptTeamsAutoLogin()
          }
        }
      } else {
        // Regular MSAL authentication
        const account = authService.getCurrentAccount()
        if (account) {
          user.value = account
          await loadUserProfile()
        }
      }
    } catch (err) {
      console.error('Auth initialization failed:', err)
      error.value = 'Failed to initialize authentication'
    } finally {
      isLoading.value = false
    }
  }

  const attemptTeamsAutoLogin = async () => {
    try {
      // console.log('Attempting Teams auto-login...')
      const result = await teamsAuthService.authenticateWithTeams()

      if (result.success && result.user) {
        // Convert Teams user to MSAL account format
        const account: AccountInfo = {
          homeAccountId: result.user.id,
          environment: 'login.microsoftonline.com',
          tenantId: import.meta.env.VITE_AZURE_TENANT_ID || 'common',
          username: result.user.upn,
          localAccountId: result.user.id,
          name: result.user.displayName,
          authorityType: 'MSSTS',
        }

        user.value = account
        userProfile.value = {
          displayName: result.user.displayName,
          mail: result.user.email,
          userPrincipalName: result.user.upn,
        }

        // Store authentication state
        localStorage.setItem('msal_token', 'authenticated')
        localStorage.setItem('msal_user', JSON.stringify(account))
        localStorage.setItem('teams_token', result.token || '')
        localStorage.setItem('teams_user', JSON.stringify(result.user))

        // console.log('Teams auto-login successful')

        // Redirect to home if not already there
        if (router.currentRoute.value.name !== 'Home') {
          router.push({ name: 'Home' })
        }
      } else {
        // console.log('Teams auto-login failed:', result.error)
        // Don't show error for auto-login failures, just fall back to manual login
      }
    } catch (err) {
      console.error('Teams auto-login error:', err)
      // Don't show error for auto-login failures, just fall back to manual login
    }
  }

  const loginWithMicrosoft = async () => {
    try {
      isLoading.value = true
      error.value = null

      if (isInTeams.value) {
        // For Teams iframe, use Teams authentication service
        // console.log('Teams context detected - using Teams authentication service')
        const result = await teamsAuthService.authenticateWithTeams()

        if (result.success && result.user) {
          // console.log('Teams authentication successful')
          // Convert Teams user to MSAL account format
          const account: AccountInfo = {
            homeAccountId: result.user.id,
            environment: 'login.microsoftonline.com',
            tenantId: import.meta.env.VITE_AZURE_TENANT_ID || 'common',
            username: result.user.upn,
            localAccountId: result.user.id,
            name: result.user.displayName,
            authorityType: 'MSSTS',
          }

          user.value = account
          userProfile.value = {
            displayName: result.user.displayName,
            mail: result.user.email,
            userPrincipalName: result.user.upn,
          }

          // Store authentication state
          localStorage.setItem('msal_token', 'authenticated')
          localStorage.setItem('msal_user', JSON.stringify(account))
          localStorage.setItem('teams_token', result.token || '')
          localStorage.setItem('teams_user', JSON.stringify(result.user))

          // Redirect to home
          router.push({ name: 'Home' })
        } else {
          console.error('Teams authentication failed:', result.error)
          error.value = result.error || 'Teams authentication failed'
        }
      } else {
        // Use regular MSAL authentication
        const account = await authService.loginWithMicrosoft()
        if (account) {
          user.value = account
          await loadUserProfile()

          // Store authentication state
          localStorage.setItem('msal_token', 'authenticated')
          localStorage.setItem('msal_user', JSON.stringify(account))

          // Redirect to home
          router.push({ name: 'Home' })
        } else {
          // If account is null, it means redirect was initiated
          // console.log('Redirect authentication initiated')
          return // Don't throw error, just return and let redirect happen
        }
      }
    } catch (err: unknown) {
      console.error('Microsoft login failed:', err)
      error.value = err instanceof Error ? err.message : 'Login failed. Please try again.'
    } finally {
      isLoading.value = false
    }
  }

  const loginWithRedirect = async () => {
    try {
      isLoading.value = true
      error.value = null
      await authService.loginWithRedirect()
    } catch (err: unknown) {
      console.error('Microsoft redirect login failed:', err)
      error.value = err instanceof Error ? err.message : 'Login failed. Please try again.'
      isLoading.value = false
    }
  }

  const loadUserProfile = async () => {
    try {
      if (user.value) {
        if (isInTeams.value) {
          // For Teams, we already have the user profile from the auth result
          const teamsUser = localStorage.getItem('teams_user')
          if (teamsUser) {
            userProfile.value = JSON.parse(teamsUser)
          }
        } else {
          userProfile.value = await authService.getUserProfile()
        }
      }
    } catch (err) {
      console.error('Failed to load user profile:', err)
    }
  }

  const logout = async () => {
    try {
      isLoading.value = true
      error.value = null

      if (isInTeams.value) {
        await teamsAuthService.logout()
      } else {
        await authService.logout()
      }

      // Clear local state
      user.value = null
      userProfile.value = null
      localStorage.removeItem('msal_token')
      localStorage.removeItem('msal_user')
      localStorage.removeItem('teams_token')
      localStorage.removeItem('teams_user')

      // Redirect to login
      router.push({ name: 'Login' })
    } catch (err: unknown) {
      console.error('Logout failed:', err)
      error.value = err instanceof Error ? err.message : 'Logout failed. Please try again.'
    } finally {
      isLoading.value = false
    }
  }

  const handleRedirectResponse = async () => {
    try {
      const account = await authService.handleRedirectResponse()
      if (account) {
        user.value = account
        await loadUserProfile()

        // Store authentication state
        localStorage.setItem('msal_token', 'authenticated')
        localStorage.setItem('msal_user', JSON.stringify(account))

        return true
      }
      return false
    } catch (err) {
      console.error('Handle redirect response failed:', err)
      return false
    }
  }

  const clearError = () => {
    error.value = null
  }

  const setTeamsAutoLogin = (enabled: boolean) => {
    localStorage.setItem('teams_auto_login', enabled.toString())
  }

  const getTeamsAutoLogin = () => {
    return localStorage.getItem('teams_auto_login') !== 'false'
  }

  return {
    user,
    userProfile,
    isAuthenticated,
    isLoading,
    error,
    isInTeams,
    loginWithMicrosoft,
    loginWithRedirect,
    logout,
    handleRedirectResponse,
    clearError,
    setTeamsAutoLogin,
    getTeamsAutoLogin,
  }
}
